// Generated by TaroBase SDK Generator: 2026-10-04T15:00:51.103Z
// Collection: donationFavorites/$favoriteId
// Edits may be overwritten by subsequent runs!

import {
  get, getMany, set, subscribe, getFiles, setFile, runQuery, count, aggregate, transformValues,
  Time, Token, Increment, Address, PublicKey,
  TimeOperation, IncrementOperation, TokenAmount, AddressType,
  TarobaseMetadata, DocumentOperation, FileItem, setMany, formatError,
} from '../db-client';
import type { AggregateResult, AggregateOperation } from '../db-client';

/**
 * Tracks a user's selected favorite users for donations. People they always want to include when donating. Max 10 per user enforced in frontend. Favorites earn bonus selfless points.
 */

export interface DonationFavoritesRequest {
  owner: AddressType;
  favoriteAddress: AddressType;
  ts: number | TimeOperation | IncrementOperation | TokenAmount;
}

export interface DonationFavoritesResponse {
  owner: string;
  favoriteAddress: string;
  ts: number;
  id: string;
  tarobase_created_at: number;
}

/**
 * Build a DonationFavorites operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildDonationFavorites(favoriteId: string, data?: DonationFavoritesRequest): DocumentOperation {
  const doc = data ? transformValues(data) : {};
  return { path: `donationFavorites/${favoriteId}`, document: doc };
}

/**
 * Authenticated users can add a favorite by specifying owner (must match their wallet) and favoriteAddress. Max 10 per user is enforced on the frontend. (Create/Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the write worked.
 */
export async function setDonationFavorites(favoriteId: string, data?: DonationFavoritesRequest): Promise<boolean> {
  try {
    const transformedData = transformValues(data || {});
    const succeeded = await set(`donationFavorites/${favoriteId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error setting DonationFavorites: ${formatError(error)}`);
    return false;
  }
}

/**
 * 
  Read Operation Details: Anyone can see which users have been favorited.
   (Get Single Item)
 */
export async function getDonationFavorites(favoriteId: string): Promise<DonationFavoritesResponse | null> {
  try {
    const result = await get(`donationFavorites/${favoriteId}`);
    return result as DonationFavoritesResponse | null;
  } catch (error) {
    console.error(`Error getting DonationFavorites: ${formatError(error)}`);
    return null;
  }
}

/**
 * Subscribes to changes in a single DonationFavorites document. (
  Read Operation Details: Anyone can see which users have been favorited.
  )
 */
export function subscribeDonationFavorites(callback: (data: DonationFavoritesResponse | null) => void, favoriteId: string): Promise<() => Promise<void>> {
  return subscribe(`donationFavorites/${favoriteId}`, { // Use the item's basePath
    onData: (data) => {
      callback(data as DonationFavoritesResponse | null);
    },
    onError: (error) => {
      console.error(`Error subscribing to DonationFavorites at path '${`donationFavorites/${favoriteId}`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get many DonationFavorites items from collection donationFavorites
 
  Read Operation Details: Anyone can see which users have been favorited.
  
 */
export async function getManyDonationFavorites(filter: string = ""): Promise<DonationFavoritesResponse[]> {
  try {
    const results = await get("donationFavorites", { prompt: filter });
    return Array.isArray(results) ? results as DonationFavoritesResponse[] : [];
  } catch (error) {
    console.error(`Error getting DonationFavorites collection at path 'donationFavorites': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in DonationFavorites collection at donationFavorites
 
  Read Operation Details: Anyone can see which users have been favorited.
  
 */
export function subscribeManyDonationFavorites(callback: (data: DonationFavoritesResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("donationFavorites", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as DonationFavoritesResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to DonationFavorites collection at path '${"donationFavorites"}': ${formatError(error)}`);
    }
  });
}

/**
 * Get all DonationFavorites items from collection donationFavorites
 
  Read Operation Details: Anyone can see which users have been favorited.
  
 */
export async function getAllDonationFavorites(filter: string = ""): Promise<DonationFavoritesResponse[]> {
  try {
    const results = await get("donationFavorites", { prompt: filter });
    return Array.isArray(results) ? results as DonationFavoritesResponse[] : [];
  } catch (error) {
    console.error(`Error getting DonationFavorites collection at path 'donationFavorites': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in DonationFavorites collection at donationFavorites
 
  Read Operation Details: Anyone can see which users have been favorited.
  
 */
export function subscribeAllDonationFavorites(callback: (data: DonationFavoritesResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("donationFavorites", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as DonationFavoritesResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to DonationFavorites collection at path '${"donationFavorites"}': ${formatError(error)}`);
    }
  });
}

/**
 * Count DonationFavorites items in collection donationFavorites.
 * Returns a numeric count instead of fetching full documents — much more efficient.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param filter - Optional natural language filter (e.g., "created in the last 7 days")
 * @returns AggregateResult with the count value
 */
export async function countDonationFavorites(filter: string = ""): Promise<AggregateResult> {
  try {
    return await count("donationFavorites", { prompt: filter || undefined });
  } catch (error) {
    console.error(`Error counting DonationFavorites at path 'donationFavorites': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * Run an aggregate operation on DonationFavorites items in collection donationFavorites.
 * Returns a single numeric value instead of fetching full documents.
 *
 * Supported operations: 'count', 'uniqueCount', 'sum', 'avg', 'min', 'max'.
 * For 'sum', 'avg', 'min', 'max', and 'uniqueCount', you must provide the field name.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param operation - The aggregate operation to perform
 * @param opts - Options including optional filter prompt and field name
 * @returns AggregateResult with the computed numeric value
 */
export async function aggregateDonationFavorites(operation: AggregateOperation, opts?: { filter?: string; field?: string }): Promise<AggregateResult> {
  try {
    return await aggregate("donationFavorites", operation, { prompt: opts?.filter || undefined, field: opts?.field });
  } catch (error) {
    console.error(`Error aggregating DonationFavorites at path 'donationFavorites': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * 
  Delete Operation Details: Only the owner can remove their own favorites.
   (Delete Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the delete worked.
 */
export async function deleteDonationFavorites(favoriteId: string): Promise<boolean> {
  try {
    const succeeded = await set(`donationFavorites/${favoriteId}`, null);
    return !!succeeded;
  } catch (error) {
    console.error(`Error deleting DonationFavorites: ${formatError(error)}`);
    return false;
  }
}

/**
 * Build a delete operation for DonationFavorites for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildDeleteDonationFavorites(favoriteId: string): DocumentOperation {
  return { path: `donationFavorites/${favoriteId}`, document: null };
}

